import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import ShippingPricingQuoteModal from '../components/ShippingPricingQuoteModal';
import { fetchQuotesForUser } from '../helpers/quoteVisibility';
import { formatQuoteDate, formatQuoteProductTypeList } from '../helpers/quoteDisplayUtils';
import { useQuoteUserRole } from '../helpers/useQuoteUserRole';

const REQUIRED_SHIPPING_FIELDS = [
  'commodityType',
  'totalUnitsPerCommodity',
  'numberOfUnitsPerBox',
  'totalNumberOfBoxesPerCommodity',
  'shipBoxDimensions',
  'shipWeightPerBox',
  'totalQtyOfBoxesPerCommodity',
  'totalValuePerProductPerSize',
  'overallValueForTheShipment',
  'shippingVolumePerProduct',
  'totalVolumeForTheWholeOrder',
  'fromShippingAddress',
  'toShippingAddress',
  'splitShipping',
];

function hasCompleteProductShipping(quote) {
  const shipping = quote.productShipping;
  if (!shipping) return false;
  return REQUIRED_SHIPPING_FIELDS.every((field) => field in shipping);
}

const StepFour = () => {
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { userRole, currentUserUid, roleFetched } = useQuoteUserRole();

  useEffect(() => {
    const fetchQuotes = async () => {
      if (!roleFetched) return;

      setLoading(true);
      try {
        if (!currentUserUid) {
          setQuotes([]);
          return;
        }

        const quotesData = await fetchQuotesForUser({ currentUserUid, userRole });
        setQuotes(quotesData.filter(hasCompleteProductShipping));
      } catch (error) {
        console.error('Error fetching quotes:', error);
        setQuotes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, [userRole, currentUserUid, roleFetched]);

  const handleEditClick = (quote) => {
    setSelectedQuote(quote);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedQuote(null);
  };

  return (
    <>
      <Header />
      <div className='parent-container pt-5 h-100 pb-10 p-4 sm:ml-64'>
        <h2 className='text-3xl p-10 mb-6' style={{ textAlign: 'center' }}>Shipping Pricing (Final Quote)</h2>
        <div className='width-full flex flex-col items-center'>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-16 h-16 border-t-4 border-b-4 border-blue-500 rounded-full animate-spin"></div>
            </div>
          ) : (
            <table style={{ maxWidth: '900px' }} className='w-full my-0 align-middle text-dark border-neutral-200'>
              <thead className='align-bottom'>
                <tr className='font-semibold text-[0.95rem] text-secondary-dark'>
                  <th className='pb-3 text-start min-w-[175px]'>Customer Name</th>
                  <th className='pb-3 text-end min-w-[100px]'>Sales Rep Name</th>
                  <th className='pb-3 text-end min-w-[100px]'>Product Type</th>
                  <th className='pb-3 pr-12 text-end min-w-[175px]'>Created On</th>
                  <th className='pb-3 text-end min-w-[50px]'>Action</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((quote) => (
                  <tr key={quote.id} className='border-b border-dashed last:border-b-0'>
                    <td className='p-3 pl-0'>{quote.customerName || quote.companyName || '—'}</td>
                    <td className='p-3 pr-0 text-end'>{quote.salesRepName || '—'}</td>
                    <td className='p-3 pr-0 text-end'>
                      {formatQuoteProductTypeList(quote.products) !== '—'
                        ? formatQuoteProductTypeList(quote.products)
                        : quote.product?.type || '—'}
                    </td>
                    <td className='p-3 pr-12 text-end'>{formatQuoteDate(quote.createdOn)}</td>
                    <td className='p-3 pr-0 text-end'>
                      <button className='ml-auto relative text-secondary-dark bg-light-dark hover:text-primary flex items-center h-[25px] w-[25px] text-base font-medium leading-normal text-center align-middle cursor-pointer rounded-2xl transition-colors duration-200 ease-in-out shadow-none border-0 justify-center' onClick={() => handleEditClick(quote)}>
                        <span className='flex items-center justify-center p-0 m-0 leading-none shrink-0 '>{userRole === 'ShippingAdmin' ? 'Edit' : 'View'}
                          <svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth='1.5' stroke='currentColor' className='w-4 h-4'>
                            <path strokeLinecap='round' strokeLinejoin='round' d='M8.25 4.5l7.5 7.5-7.5 7.5' />
                          </svg>
                        </span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && selectedQuote && (
          <ShippingPricingQuoteModal quote={selectedQuote} userRole={userRole} onClose={handleCloseModal} />
        )}
      </div>
    </>
  );
};

export default StepFour;
